import React, { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiRefreshCw,
  FiShield,
} from "react-icons/fi";
import PayoutBulkUploadFlowSteps from "./PayoutBulkUploadFlowSteps";
import { payoutApi } from "./api/payoutApiClient";
import {
  compactBody,
  errorMessage,
  isPayoutMockMode,
  pickString,
  readSessionJson,
  rupeesToPaise,
  stableIdempotencyKey,
  verifyStepUp,
  writeSessionJson,
} from "./api/payoutMutationHelpers";
import { validateOtpCode } from "./api/payoutValidation";
import { showPayoutNotification } from "./PayoutNotification";

const defaultBulkTransferDraft = {
  fileName: "-",
  payoutAccount: null as null | {
    name?: string;
    bank?: string;
    accountNumber?: string;
  },
  rows: [] as Record<string, unknown>[],
};

function SummaryRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-6 border-b border-[#E0E5F2] py-3 last:border-b-0">
      <span className="text-sm font-medium text-[#505E8E]">{label}</span>
      <div className="max-w-[220px] text-right text-sm font-semibold text-[#1B163B]">
        {value}
      </div>
    </div>
  );
}

const formatINR = (amount: number) =>
  `\u20B9${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const displayDraftPayoutAccount = (account: typeof defaultBulkTransferDraft.payoutAccount) => {
  if (!account) return "-";
  const digits = String(account.accountNumber || "").replace(/\D/g, "");
  const last4 = digits ? digits.slice(-4) : String(account.accountNumber || "").slice(-4);
  return [account.name, account.bank, last4 ? `**** ${last4}` : ""]
    .filter(Boolean)
    .join(" | ") || "-";
};

const buildBulkPaymentItem = (row: Record<string, unknown>, index: number) =>
  compactBody({
    funding_account_id: pickString(row, [
      "funding_account_id",
      "fundingAccountId",
      "source_account_id",
      "payout_account_id",
      "account_id",
    ]),
    funding_account_name: pickString(row, [
      "funding_account_name",
      "fundingAccountName",
      "source_account_name",
      "payout_account_name",
      "account_name",
    ]),
    beneficiary_id: pickString(row, ["beneficiary_id", "beneficiaryId", "id"]),
    beneficiary_name: pickString(row, ["beneficiary_name", "beneficiaryName"]),
    beneficiary_account_number: pickString(row, [
      "beneficiary_account_number",
      "beneficiaryAccountNumber",
      "account_number",
    ]),
    beneficiary_ifsc: pickString(row, [
      "beneficiary_ifsc",
      "beneficiaryIfsc",
      "ifsc",
    ]),
    amount_paise:
      Number(row.amount_paise) ||
      Number(row.amountPaise) ||
      rupeesToPaise(row.amount),
    mode: pickString(row, [
      "mode",
      "payout_method",
      "payment_method",
      "transfer_method",
    ], "NEFT"),
    client_reference_id: pickString(
      row,
      ["client_reference_id", "clientReferenceId", "reference_id"],
      `bulk-row-${index + 1}`,
    ),
    purpose: pickString(row, ["purpose", "narration", "remarks"]),
  });

const normalizedCreateItem = (row: Record<string, unknown>) =>
  compactBody({
    funding_account_id: pickString(row, ["funding_account_id"]),
    beneficiary_id: pickString(row, ["beneficiary_id"]),
    amount_paise: Number(row.amount_paise) || 0,
    mode: pickString(row, ["mode"]),
    client_reference_id: pickString(row, ["client_reference_id"]),
    purpose: pickString(row, ["purpose"]),
    beneficiary_account_number: pickString(row, [
      "beneficiary_account_number",
      "beneficiaryAccountNumber",
      "account_number",
    ]),
    beneficiary_ifsc: pickString(row, [
      "beneficiary_ifsc",
      "beneficiaryIfsc",
      "ifsc",
    ]),
  });

function PayoutBulkUploadVerify() {
  const navigate = useNavigate();
  const draft = useMemo(
    () => readSessionJson("payoutBulkTransferDraft", defaultBulkTransferDraft),
    [],
  );
  const bulkRows = Array.isArray(draft.rows) ? draft.rows : [];
  const totalAmount = bulkRows.reduce(
    (sum: number, row: Record<string, unknown>) =>
      sum + (Number(row.amount_paise) || Number(row.amountPaise) || 0) / 100,
    0,
  );
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const method = "authenticator" as const;
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const code = useMemo(() => otp.join(""), [otp]);
  const ready = !validateOtpCode(code);
  const [submitting, setSubmitting] = useState(false);
  const [, setSubmitError] = useState("");
  const showSubmitError = React.useCallback((message: string) => {
    setSubmitError(message);
    showPayoutNotification({ tone: "error", message });
  }, []);
  const instruction = "Enter the 6-digit code from your Google Authenticator app.";

  const updateDigit = (index: number, value: string) => {
    const nextValue = value.replace(/\D/g, "").slice(-1);
    const nextOtp = [...otp];
    nextOtp[index] = nextValue;
    setOtp(nextOtp);
    if (nextValue && index < inputsRef.current.length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 6).split("");
    if (!digits.length) return;
    const nextOtp = ["", "", "", "", "", ""];
    digits.forEach((digit, index) => {
      nextOtp[index] = digit;
    });
    setOtp(nextOtp);
    inputsRef.current[Math.min(digits.length, 5)]?.focus();
  };

  const handleVerifyAndSubmit = async () => {
    setSubmitError("");
    const otpError = validateOtpCode(
      code,
      "Bulk upload transfers: enter the 6-digit Google Authenticator code to submit this batch.",
    );
    if (otpError) {
      showSubmitError(otpError);
      return;
    }
    if (!bulkRows.length) {
      showSubmitError("Bulk upload transfers: upload and review a transfer file before submitting.");
      return;
    }
    if (submitting) return;

    if (isPayoutMockMode()) {
      navigate("/payouts/transfers/bulk-upload/confirm");
      return;
    }

    setSubmitting(true);
    try {
      const validationItems = bulkRows.map(buildBulkPaymentItem);
      const validationBody = { items: validationItems };
      const validation = await payoutApi.validateBulkPayments(
        validationBody,
        stableIdempotencyKey("validate-bulk-payments", draft.fileName),
      );
      const validationRows = Array.isArray(validation.rows)
        ? validation.rows.filter(
            (row): row is Record<string, unknown> =>
              !!row && typeof row === "object",
          )
        : [];
      const invalidRows = validationRows.filter((row) => row.ok !== true);

      if (invalidRows.length) {
        const firstInvalid = invalidRows[0];
        const rowNumber = Number(firstInvalid.row_number) || 1;
        const errors = Array.isArray(firstInvalid.errors)
          ? firstInvalid.errors.filter((error): error is string => typeof error === "string")
          : [pickString(firstInvalid, ["status_label"], "Invalid bulk row")];
        throw new Error(`Row ${rowNumber}: ${errors.join(", ")}`);
      }

      const createItems = validationRows.length
        ? validationRows.map((row) =>
            normalizedCreateItem((row.normalized ?? {}) as Record<string, unknown>),
          )
        : validationItems.map((row) =>
            normalizedCreateItem(row as Record<string, unknown>),
          );
      const createBody = { items: createItems };
      const stepUpToken = await verifyStepUp({
        purpose: "CREATE_BULK_PAYOUT",
        method,
        code,
        subjectId: draft.fileName,
      });
      const response = await payoutApi.createBulkPayments(
        createBody,
        stableIdempotencyKey("create-bulk-payments", draft.fileName),
        stepUpToken,
      );
      writeSessionJson("payoutBulkTransferResult", response);
      navigate("/payouts/transfers/bulk-upload/confirm");
    } catch (error) {
      showSubmitError(errorMessage(error, "Bulk upload transfers: unable to submit this batch. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7">
        <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
          Verify Bulk Upload
        </h1>
        <p className="mt-1 text-sm font-medium text-[#505E8E]">
          Verify this bulk transfer batch before it is submitted for approval.
        </p>
      </div>

      <PayoutBulkUploadFlowSteps activeStep={3} />

      <div className="grid gap-5 xl:grid-cols-[minmax(0,760px)_380px]">
        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <div className="mb-6 flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[8px] bg-[#1B163B0D] text-xl text-[#1B163B]">
              <FiShield />
            </div>
            <div>
              <h2 className="text-[18px] font-semibold text-[#1B163B]">
                Verify Bulk Transfer Request
              </h2>
              <p className="mt-1 text-sm font-medium text-[#505E8E]">
                {instruction}
              </p>
            </div>
          </div>

          <div>
            <div className="rounded-[8px] border border-[#1B163B] bg-[#1B163B0D] px-5 py-4 text-left">
              <div className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-[6px] bg-[#DCFCE7] text-[#16A34A]">
                    <FiShield />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-[#1B163B]">
                      Google Authenticator
                    </span>
                    <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                      Recommended for high-value batches
                    </span>
                  </span>
                </span>
                <FiCheck className="text-lg text-[#16A34A]" />
              </div>
            </div>
          </div>

          <div className="mt-8">
            <label className="mb-3 block text-sm font-semibold text-[#1B163B]">
              Verification Code
            </label>
            <div className="flex flex-wrap gap-3">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(element) => {
                    inputsRef.current[index] = element;
                  }}
                  value={digit}
                  onChange={(event) => updateDigit(index, event.target.value)}
                  onPaste={(event) => {
                    event.preventDefault();
                    handlePaste(event.clipboardData.getData("text"));
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Backspace" && !otp[index] && index > 0) {
                      inputsRef.current[index - 1]?.focus();
                    }
                  }}
                  inputMode="numeric"
                  className="h-12 w-12 rounded-[6px] border border-[#E0E5F2] bg-white text-center text-lg font-semibold text-[#1B163B] outline-none focus:border-[#1B163B]"
                />
              ))}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-5 text-sm font-semibold">
              <button
                type="button"
                onClick={() => setOtp(["", "", "", "", "", ""])}
                className="flex items-center gap-2 text-[#1B163B]"
              >
                <FiRefreshCw />
                Resend code
              </button>
            </div>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-4">
            <Link
              to="/payouts/transfers/bulk-upload/review"
              className="flex h-10 items-center gap-2 rounded-[6px] border border-[#E0E5F2] bg-white px-7 text-sm font-semibold text-[#1B163B]"
            >
              <FiArrowLeft />
              Back
            </Link>
            <button
              type="button"
              onClick={handleVerifyAndSubmit}
              disabled={!ready || submitting}
              style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
              className={
                "flex h-10 min-w-[180px] items-center justify-center gap-3 rounded-[6px] px-7 text-sm font-semibold text-white " +
                (ready && !submitting ? "" : "opacity-50")
              }
            >
              {submitting ? "Submitting..." : "Verify & Continue"}
              <FiArrowRight />
            </button>
          </div>
        </section>

        <aside className="h-fit rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <h2 className="text-[18px] font-semibold text-[#1B163B]">
            Batch Summary
          </h2>
          <div className="my-5 h-px bg-[#E0E5F2]" />
          <SummaryRow label="Transfers" value={bulkRows.length || "-"} />
          <SummaryRow label="Payout Account" value={displayDraftPayoutAccount(draft.payoutAccount)} />
          <SummaryRow label="Total Amount" value={formatINR(totalAmount)} />
          <SummaryRow label="Verification" value="Google Authenticator" />
          <SummaryRow label="Approval" value="Pending after submit" />
          <div className="mt-5 rounded-[6px] bg-[#DCFCE7] px-4 py-3 text-sm font-medium text-[#166534]">
            Bulk upload is locked while verification is in progress.
          </div>
        </aside>
      </div>
    </div>
  );
}

export default PayoutBulkUploadVerify;
